import { useCallback, useEffect } from 'react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import {
  uploadFile,
  getIntake,
  getIntakes,
  updateIntake,
  updateIntakeStatus,
  type IntakeResponse,
} from '../services/api';
import { confirmTabStatusEndpointIntakeIntakeIdTabTabKeyConfirmPut } from '../services/api/sdk.gen';
import type { IntakeTabKey } from '../services/api/types.gen';
import type { IntakeRecord } from '../store/slices/intakeSlice';
import { INTAKE_STATUS } from '../constants/intakeStatus';
import { normalizeGender } from '../utils/intake';

export interface IntakeFilters {
  search?: string;
  startDate?: string; // ISO date string YYYY-MM-DD
  endDate?: string; // ISO date string YYYY-MM-DD
  statuses?: string[]; // Filter by one or more status values
  clinicId?: string;
  assigneeIds?: string[];
  assigneeIncludeUnassigned?: boolean;
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedIntakesResult {
  items: IntakeRecord[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
}

function ensureIntakeRequiredFields(intake: IntakeResponse): IntakeRecord {
  return {
    ...intake,
    fax_pdf_path: intake.fax_pdf_path || '',
    file_size: intake.file_size || 0,
    ocr_text: intake.ocr_text || '',
    patient_first_name: intake.patient_first_name || '',
    patient_last_name: intake.patient_last_name || '',
    patient_ssn: intake.patient_ssn || '',
    patient_phone: intake.patient_phone || '',
    patient_secondary_phone: intake.patient_secondary_phone ?? '',
    primary_insurance: intake.primary_insurance || '',
    primary_member_id: intake.primary_member_id || '',
    primary_payor_id: intake.primary_payor_id || undefined,
    secondary_insurance: intake.secondary_insurance || '',
    secondary_member_id: intake.secondary_member_id || '',
    secondary_payor_id: intake.secondary_payor_id || undefined,
    referring_physician: intake.referring_physician || '',
    referring_phone: intake.referring_phone || '',
    referring_fax: intake.referring_fax || '',
    discharge_facility_name: intake.discharge_facility_name || '',
    discharge_facility_type: intake.discharge_facility_type || '',
    referral_source: intake.referral_source || '',
    primary_diagnosis: intake.primary_diagnosis || '',
    diagnosis_codes: intake.diagnosis_codes || [],
    services_requested: intake.services_requested || [],
    frequency_duration: intake.frequency_duration || '',
    fax_number_from: intake.fax_number_from || '',
    fax_number_to: intake.fax_number_to || '',
    efax_message_id: intake.efax_message_id || '',
    total_pages: intake.total_pages || 1,
    escalated_reason: intake.escalated_reason || '',
    priority: 'normal',
    patient_gender: normalizeGender(intake.patient_gender) || undefined,
    patient_dob: intake.patient_dob || undefined,
    patient_id: intake.patient_id || undefined,
    assigned_to: intake.assigned_to || undefined,
    assigned_date: intake.assigned_date || undefined,
    requires_prior_auth: intake.requires_prior_auth || false,
    insurance_verified: intake.insurance_verified || false,
    homebound_status_verified: intake.homebound_status_verified || false,
    in_service_area: intake.in_service_area || false,
    sla_deadline: intake.sla_deadline || undefined,
    escalated: intake.escalated || false,
    escalated_date: intake.escalated_date || undefined,
    is_duplicate: intake.is_duplicate || false,
    original_intake_id: intake.original_intake_id || undefined,
    reviewed_by: intake.reviewed_by || undefined,
    reviewed_date: intake.reviewed_date || undefined,
    decision_date: intake.decision_date || undefined,
    decision_notes: intake.decision_notes || undefined,
    rejection_reason: intake.rejection_reason || undefined,
    rejection_details: intake.rejection_details || undefined,
    created_admission_id: intake.created_admission_id || undefined,
    authorization_status: intake.authorization_status || undefined,
    authorization_number: intake.authorization_number || undefined,
    authorization_request_date: intake.authorization_request_date || undefined,
    authorization_last_updated: intake.authorization_last_updated || undefined,
    authorization_notes: intake.authorization_notes || undefined,
    sn_visits: intake.sn_visits ?? undefined,
    pt_visits: intake.pt_visits ?? undefined,
    ot_visits: intake.ot_visits ?? undefined,
    st_visits: intake.st_visits ?? undefined,
    msw_visits: intake.msw_visits ?? undefined,
    hha_visits: intake.hha_visits ?? undefined,
    dietician_visits: intake.dietician_visits ?? undefined,
    misc_visits: intake.misc_visits ?? undefined,
    authorization_intake_document_id:
      intake.authorization_intake_document_id ?? undefined,
    create_authorization_on_onboarding:
      intake.create_authorization_on_onboarding ?? true,
    authorization_document_id: intake.authorization_document_id || undefined,
    risk_score: intake.risk_score || undefined,
    ai_summary: intake.ai_summary || undefined,
    ai_recommendations: intake.ai_recommendations || undefined,
    last_ai_analysis_date: intake.last_ai_analysis_date || undefined,
    last_ai_analysis_status: intake.last_ai_analysis_status || undefined,
    documentUrl: intake.fax_pdf_path || undefined,
    error: undefined,
  } as IntakeRecord;
}

// Shared across all useIntake hook instances in this module.
// This ensures response ordering is enforced globally per intake id.
const latestUpdateRequestSeqByIntakeId: Record<string, number> = {};

export const useIntake = () => {
  const {
    // State
    uploadStatus,
    uploadProgress,
    uploadError,
    isExtracting,
    intakes,
    selectedIntake,
    intakesLoading,
    intakesError,

    // Actions
    setUploadStatus,
    setUploadProgress,
    setUploadError,
    setIsExtracting,
    setIntakes,
    addIntake,
    updateIntakeInStore,
    setSelectedIntake,
    setIntakesLoading,
    setIntakesError,
    resetUploadState,
    user,
  } = useStore(
    useShallow(state => ({
      // State
      uploadStatus: state.uploadStatus,
      uploadProgress: state.uploadProgress,
      uploadError: state.uploadError,
      isExtracting: state.isExtracting,
      intakes: state.intakes,
      selectedIntake: state.selectedIntake,
      intakesLoading: state.intakesLoading,
      intakesError: state.intakesError,
      user: state.user,

      // Actions
      setUploadStatus: state.setUploadStatus,
      setUploadProgress: state.setUploadProgress,
      setUploadError: state.setUploadError,
      setIsExtracting: state.setIsExtracting,
      setIntakes: state.setIntakes,
      addIntake: state.addIntake,
      updateIntakeInStore: state.updateIntake,
      setSelectedIntake: state.setSelectedIntake,
      setIntakesLoading: state.setIntakesLoading,
      setIntakesError: state.setIntakesError,
      resetUploadState: state.resetUploadState,
    }))
  );

  const intakeExistingPatientWorkflowEnabled =
    user?.agency?.is_kinnser_enabled === true ||
    user?.agency?.is_rogeremr_enabled === true;

  const ensureRequiredFields = useCallback(
    (intake: IntakeResponse): IntakeRecord =>
      ensureIntakeRequiredFields(intake),
    []
  );

  // Upload file and create intake
  const uploadDocument = useCallback(
    async (file: File) => {
      try {
        setUploadStatus('uploading');
        setUploadError(null);
        setUploadProgress(0);

        // Upload file directly through backend (avoids CORS issues)
        const intakeResponse = await uploadFile({
          body: { file },
        });

        if (
          !intakeResponse ||
          !intakeResponse.data ||
          !intakeResponse.data.intake
        ) {
          throw new Error('Upload failed - no intake data received');
        }

        // Get the fresh intake data
        const freshIntake = await getIntake({
          path: { intake_id: intakeResponse.data.intake.id },
        });

        if (!freshIntake || !freshIntake.data) {
          throw new Error('Failed to fetch fresh intake data');
        }

        // Ensure required fields have defaults and convert to IntakeRecord
        const freshIntakeRecord = ensureRequiredFields(freshIntake.data);

        addIntake(freshIntakeRecord);
        setSelectedIntake(freshIntakeRecord);
        setUploadStatus('success');

        // Start polling for extraction results
        pollIntakeStatus(freshIntakeRecord.id);

        return freshIntakeRecord;
      } catch (error) {
        console.error('Failed to upload document:', error);
        setUploadError('Failed to upload document. Please try again.');
        setUploadStatus('error');
        throw error;
      }
    },
    [
      setUploadStatus,
      setUploadError,
      setUploadProgress,
      addIntake,
      setSelectedIntake,
    ]
  );

  // Poll intake status for extraction results
  // Note: This is legacy polling, mostly replaced by workflow status queries
  const pollIntakeStatus = useCallback(
    async (intakeId: string) => {
      setIsExtracting(true);

      const pollInterval = setInterval(async () => {
        try {
          const intake = await getIntake({
            path: { intake_id: intakeId },
          });
          if (!intake || !intake.data) {
            console.error('No intake data received');
            return;
          }

          // Update intake in store
          const updatedIntake = ensureRequiredFields(intake.data);
          updateIntakeInStore(intakeId, updatedIntake);

          // If extraction is complete, intake is processed, or there are processing errors, stop polling
          if (
            intake.data.status === 'accepted' ||
            intake.data.status === 'rejected' ||
            intake.data.is_processed ||
            intake.data.processing_errors
          ) {
            clearInterval(pollInterval);
            setIsExtracting(false);
          }
        } catch (error) {
          console.error('Error polling intake status:', error);
          clearInterval(pollInterval);
          setIsExtracting(false);
        }
      }, 10000); // Poll every 10 seconds

      // Return cleanup function so caller can stop polling if needed
      return () => {
        clearInterval(pollInterval);
        setIsExtracting(false);
      };
    },
    [updateIntakeInStore, setIsExtracting]
  );

  // Load intakes with filters and pagination
  const loadIntakes = useCallback(
    async (filters: IntakeFilters = {}): Promise<PaginatedIntakesResult> => {
      const {
        search,
        startDate,
        endDate,
        statuses,
        clinicId,
        assigneeIds,
        assigneeIncludeUnassigned,
        page = 1,
        perPage = 20,
        sortBy = 'created_at',
        sortOrder = 'asc',
      } = filters;

      try {
        setIntakesLoading(true);
        setIntakesError(null);

        const response = await getIntakes({
          query: {
            search: search || undefined,
            start_date: startDate || undefined,
            end_date: endDate || undefined,
            intake_status:
              statuses && statuses.length > 0 ? statuses : undefined,
            clinic_id: clinicId || undefined,
            assignee_ids:
              assigneeIds && assigneeIds.length > 0 ? assigneeIds : undefined,
            include_unassigned: assigneeIncludeUnassigned ?? undefined,
            page,
            per_page: perPage,
            sort_by: sortBy,
            sort_order: sortOrder,
          },
        });

        if (!response.data) {
          const emptyResult: PaginatedIntakesResult = {
            items: [],
            total: 0,
            page: 1,
            perPage,
            totalPages: 1,
          };
          setIntakes([]);
          return emptyResult;
        }

        const intakeRecords: IntakeRecord[] = response.data.items.map(intake =>
          ensureRequiredFields(intake)
        );

        setIntakes(intakeRecords);

        return {
          items: intakeRecords,
          total: response.data.total,
          page: response.data.page,
          perPage: response.data.per_page,
          totalPages: response.data.total_pages,
        };
      } catch (error) {
        console.error('Error loading intakes:', error);
        setIntakesError('Failed to load intakes. Please try again.');
        setIntakes([]);
        return {
          items: [],
          total: 0,
          page: 1,
          perPage,
          totalPages: 1,
        };
      } finally {
        setIntakesLoading(false);
      }
    },
    [setIntakesLoading, setIntakesError, setIntakes, ensureRequiredFields]
  );

  // Get intake details
  const loadIntakeDetails = useCallback(
    async (id: string) => {
      try {
        const intake = await getIntake({
          path: { intake_id: id },
        });
        if (!intake || !intake.data) {
          throw new Error('Failed to fetch intake details');
        }

        // Ensure required fields have defaults and convert to IntakeRecord
        const intakeRecord = ensureRequiredFields(intake.data);

        setSelectedIntake(intakeRecord);
        return intakeRecord;
      } catch (error) {
        console.error('Error loading intake details:', error);
        throw error;
      }
    },
    [ensureRequiredFields, setSelectedIntake]
  );

  // Update intake
  const updateIntakeRecord = useCallback(
    async (
      id: string,
      updates: Partial<IntakeResponse>
    ): Promise<IntakeRecord> => {
      try {
        const requestSeq = (latestUpdateRequestSeqByIntakeId[id] ?? 0) + 1;
        latestUpdateRequestSeqByIntakeId[id] = requestSeq;

        const response = await updateIntake({
          path: { intake_id: id },
          body: updates,
        });
        if (!response || !response.data) {
          throw new Error('Failed to update intake');
        }

        // Convert to IntakeRecord format
        const intakeRecord = ensureRequiredFields(response.data);

        // Only apply the most recent response for this intake. Older responses
        // are valid API results, but stale for UI state and can clobber fields
        // currently being edited or saved by newer requests.
        if (latestUpdateRequestSeqByIntakeId[id] === requestSeq) {
          // Update the list/cache and replace the detail view only when this
          // intake is still selected. The store action performs that ID guard,
          // preventing an old PATCH response from navigating back to its intake.
          updateIntakeInStore(id, intakeRecord);
        }

        return intakeRecord;
      } catch (error) {
        console.error('Error updating intake:', error);
        throw error;
      }
    },
    [updateIntake, ensureRequiredFields, updateIntakeInStore]
  );

  // Update intake status
  const updateIntakeStatusRecord = useCallback(
    async (
      id: string,
      statusUpdate: (typeof INTAKE_STATUS)[keyof typeof INTAKE_STATUS]
    ): Promise<IntakeRecord> => {
      try {
        await updateIntakeStatus({
          path: { intake_id: id },
          body: { new_status: statusUpdate },
        });

        // Fetch the updated intake to get the latest data
        const updatedIntake = await getIntake({
          path: { intake_id: id },
        });
        if (!updatedIntake || !updatedIntake.data) {
          throw new Error('Failed to fetch updated intake');
        }

        const intakeRecord = ensureRequiredFields(updatedIntake.data);
        setSelectedIntake(intakeRecord);
        return intakeRecord;
      } catch (error) {
        console.error('Error updating intake status:', error);
        throw error;
      }
    },
    [updateIntake, ensureRequiredFields, setSelectedIntake]
  );

  // Confirm tab completion status
  const confirmTabStatus = useCallback(
    async (
      id: string,
      tabKey: IntakeTabKey,
      status:
        | 'complete'
        | 'incomplete'
        | 'complete_with_errors'
        | 'skipped' = 'complete'
    ): Promise<IntakeRecord> => {
      try {
        const response =
          await confirmTabStatusEndpointIntakeIntakeIdTabTabKeyConfirmPut({
            path: { intake_id: id, tab_key: tabKey },
            body: { status },
          });
        if (!response || !response.data) {
          throw new Error('Failed to confirm tab status');
        }

        // Convert to IntakeRecord format
        const intakeRecord = ensureRequiredFields(response.data);

        // Update the store to keep state in sync
        setSelectedIntake(intakeRecord);

        return intakeRecord;
      } catch (error) {
        console.error('Error confirming tab status:', error);
        throw error;
      }
    },
    [ensureRequiredFields, setSelectedIntake]
  );

  // When neither EMR supports existing-patient intake for this agency, is_new_patient can only be true (new).
  // Normalize null to true so the intake record stays consistent.
  useEffect(() => {
    if (!selectedIntake || intakeExistingPatientWorkflowEnabled) {
      return;
    }
    const isNewPatient = (selectedIntake as { is_new_patient?: boolean | null })
      .is_new_patient;
    if (isNewPatient === null || isNewPatient === undefined) {
      void updateIntakeRecord(selectedIntake.id, { is_new_patient: true });
    }
  }, [
    selectedIntake,
    intakeExistingPatientWorkflowEnabled,
    updateIntakeRecord,
  ]);

  return {
    // State
    uploadStatus,
    uploadProgress,
    uploadError,
    isExtracting,
    intakes,
    selectedIntake,
    intakesLoading,
    intakesError,

    // Actions
    uploadDocument,
    loadIntakes,
    loadIntakeDetails,
    updateIntakeRecord,
    updateIntakeStatusRecord,
    confirmTabStatus,
    resetUploadState,
    setSelectedIntake,
    pollIntakeStatus,
  };
};
